import type { ComparisonDetail } from "../../domain/insights/comparison-types.js";

export interface CourseHoleAverage {
  hole: number;
  n: number;
  left: number | null;
  right: number | null;
}

/** Tee groups are evidence partitions, not separate courses. Count each physical hole once. */
export function courseHoleAverages(rows: ComparisonDetail["holes"]) {
  const courses = new Map<string, { name: string; rows: typeof rows }>();
  for (const row of rows) {
    const course = courses.get(row.courseId) ?? {
      name: row.courseName,
      rows: [],
    };
    course.rows.push(row);
    courses.set(row.courseId, course);
  }
  return [...courses]
    .map(([id, course]) => ({
      id,
      name: course.name,
      holes: [...new Set(course.rows.map((row) => row.holeNumber))]
        .sort((a, b) => a - b)
        .map((hole) => averageHole(hole, course.rows)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function averageHole(
  hole: number,
  rows: ComparisonDetail["holes"],
): CourseHoleAverage {
  const evidence = new Map<
    string,
    ComparisonDetail["holes"][number]["observation"]["evidence"][number]
  >();
  for (const row of rows.filter((row) => row.holeNumber === hole)) {
    for (const sample of row.observation.evidence) {
      if (!Number.isFinite(sample.left) || !Number.isFinite(sample.right))
        continue;
      const previous = evidence.get(sample.roundId);
      if (!previous || sample.roundVersion > previous.roundVersion)
        evidence.set(sample.roundId, sample);
    }
  }
  const values = [...evidence.values()],
    n = values.length;
  return {
    hole,
    n,
    left: n ? values.reduce((sum, sample) => sum + sample.left, 0) / n : null,
    right: n ? values.reduce((sum, sample) => sum + sample.right, 0) / n : null,
  };
}
